/**
 * BUSCA, ORDENAÇÃO, RECORTE e RESUMO da lista de ORDENS DE SERVIÇO
 * (`/ordens-servico`).
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters`, do
 * `clientsListFilters` e do `worksListFilters`: são regras que decidem QUAIS
 * ordens o prestador vê e em que ordem, e todas erram em silêncio — uma lista
 * recortada errado não dá erro, só esconde serviço.
 *
 * ─── O QUE É ESPECÍFICO DESTA LISTA ─────────────────────────────────────────
 *
 * · **`cancelled` é o status MORTO daqui.** O hero soma dinheiro, e uma OS
 *   cancelada é serviço que não vai ser cobrado — somá-la diria ao prestador que
 *   ele tem mais na mão do que tem (o corolário do #182/#188). Quantas ficaram
 *   fora é DEVOLVIDO para a tela dizer: "no silent caps" (#61).
 * · **A busca por `#N` é EXATA e EXCLUSIVA**, igual à de `/quotes` (#190c):
 *   `service_orders.sequence_id` é inteiro exibido com 4 casas, então casar como
 *   texto faria "#7" trazer #0017 e #0070 e a busca leria como aleatória. E quem
 *   digita "#12" quer o documento 12, não todo cliente com "12" no nome — por
 *   isso o `#` desliga o casamento textual. O parser é o MESMO de `/quotes`
 *   (`parseSequenceQuery`): duas cópias divergiriam na primeira correção.
 * · **A data do recorte é `created_at` (TIMESTAMP), não `scheduled_date`.** As
 *   duas existem na tabela e significam coisas diferentes: "quando eu abri" e
 *   "quando vou executar". O filtro de período responde à primeira (é o mesmo
 *   contrato de `/quotes` e `/trabalhos`), e o agendamento aparece no card como
 *   informação — misturar as duas faria uma OS sem agendamento sumir do recorte
 *   sem explicação nenhuma.
 *
 * O parser de dinheiro é o `quoteTotalNumber` (#123a) — `total` chega como
 * `number` do formulário e como STRING do PostgREST — e a normalização de texto
 * é a mesma da busca de documentos (#190d): dobra caixa E acento, senão quem
 * digita "manutencao" no teclado do celular recebe zero por "Manutenção".
 */
import type { ServiceOrderStatus } from '@/hooks/useServiceOrders';
import { normalizeSearchText, parseSequenceQuery } from '@/lib/quoteSearch';
import { quoteTotalNumber } from '@/lib/quotesListSummary';
import { matchesListRange, type ListValueRange } from '@/lib/listFilters';
import type { ReportDateRange, ReportPeriodPreset } from '@/lib/reportPeriod';

/**
 * O que este módulo enxerga de uma ordem de serviço.
 *
 * As linhas chegam do cache tipadas como `any` (e do PostgREST com `numeric`
 * como string), então tudo aqui é lido com cuidado — nada é `Number()` cru.
 */
export interface ServiceOrderRow {
  id?: string | null;
  title?: string | null;
  client_name?: string | null;
  sequence_id?: number | null;
  status?: string | null;
  total?: unknown;
  created_at?: string | null;
  scheduled_date?: string | null;
}

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo (é o estado sem filtro).
 *
 * Campos varridos: número, título e nome do cliente — exatamente o que o card
 * mostra. Buscar em campo que a linha não exibe devolveria resultados que o
 * usuário não consegue explicar.
 */
export function serviceOrderMatchesSearch(order: ServiceOrderRow, term: string): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;

  const wantedSequence = parseSequenceQuery(term);
  if (wantedSequence !== null && order?.sequence_id === wantedSequence) return true;
  // O `#` explícito NÃO cai no texto: quem pergunta por número quer o documento
  // daquele número, e devolver os dois misturados é o que faz a busca parecer
  // quebrada (#190c).
  if (term.trim().startsWith('#')) return false;

  if (normalizeSearchText(order?.title).includes(needle)) return true;
  if (normalizeSearchText(order?.client_name).includes(needle)) return true;
  return false;
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type ServiceOrderSortOption =
  | 'recent'
  | 'oldest'
  | 'highest'
  | 'lowest'
  | 'client'
  | 'title';

export const SERVICE_ORDER_SORT_LABELS: Record<ServiceOrderSortOption, string> = {
  recent: 'Mais recentes',
  oldest: 'Mais antigas',
  highest: 'Maior valor',
  lowest: 'Menor valor',
  client: 'Nome do cliente',
  title: 'Título da ordem',
};

export const SERVICE_ORDER_SORT_ORDER: readonly ServiceOrderSortOption[] = [
  'recent',
  'oldest',
  'highest',
  'lowest',
  'client',
  'title',
];

/**
 * `recent` é o PADRÃO porque é como a lista sempre se comportou (a consulta já
 * devolve `created_at desc`). Trocar o padrão junto com o redesenho mudaria a
 * tela de todo mundo sem ninguém ter pedido.
 */
export const DEFAULT_SERVICE_ORDER_SORT: ServiceOrderSortOption = 'recent';

/**
 * Data ilegível vai para o FIM em "mais recentes" (e não para o topo, que é onde
 * `NaN → 0` a colocaria em "mais antigas"): ordem sem data é exceção, e exceção
 * não ocupa a primeira linha da lista.
 */
function timeOf(order: ServiceOrderRow): number {
  const parsed = new Date(order?.created_at ?? '').getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Vazio vai para o FIM (`￿`): um punhado de "sem título" no topo esconde o resto. */
function textOf(value: unknown): string {
  return normalizeSearchText(value) || '￿';
}

function totalOf(order: ServiceOrderRow): number {
  return quoteTotalNumber(order?.total);
}

/** Não muta a entrada: a lista da tela é derivada da consulta. */
export function sortServiceOrdersBy<T extends ServiceOrderRow>(
  orders: readonly T[],
  option: ServiceOrderSortOption,
): T[] {
  const rows = [...orders];
  // Desempate ESTÁVEL por id (#93): sem ele, duas ordens do mesmo dia (ou do
  // mesmo valor — valores redondos repetem muito) trocam de lugar entre renders
  // e a lista "pisca" sem ninguém ter mexido em nada.
  const byId = (a: T, b: T) => String(a?.id).localeCompare(String(b?.id));

  switch (option) {
    case 'oldest':
      return rows.sort((a, b) => timeOf(a) - timeOf(b) || byId(a, b));
    case 'highest':
      return rows.sort((a, b) => totalOf(b) - totalOf(a) || byId(a, b));
    case 'lowest':
      return rows.sort((a, b) => totalOf(a) - totalOf(b) || byId(a, b));
    case 'client':
      return rows.sort(
        (a, b) => textOf(a?.client_name).localeCompare(textOf(b?.client_name)) || byId(a, b),
      );
    case 'title':
      return rows.sort((a, b) => textOf(a?.title).localeCompare(textOf(b?.title)) || byId(a, b));
    case 'recent':
    default:
      return rows.sort((a, b) => timeOf(b) - timeOf(a) || byId(a, b));
  }
}

// ─── Recorte por STATUS (os botões da faixa) ────────────────────────────────

export type ServiceOrderStatusFilter = 'all' | ServiceOrderStatus;

/**
 * Os botões da faixa, na ordem do ciclo de vida da ordem.
 *
 * ─── O RÓTULO CURTO É MEDIDO, NÃO ESCOLHIDO ────────────────────────────────
 *
 * Em 360px a faixa tem 336px úteis, e os cinco rótulos custam (medido por
 * réplica de DOM com a fonte real, chips `text-[11px]` + `px-1.5`, gaps
 * incluídos):
 *
 *   Todas · Pendentes · Andamento  · Concluídas · Canceladas → 343  ✗ (rola)
 *   Todas · Pendentes · Em curso   · Concluídas · Canceladas → 330  ✓ (+6)
 *   Todas · Pendentes · Execução   · Concluídas · Canceladas → 330  ✓ (+6)
 *   Todas · Pendentes · Andam.     · Concluídas · Canceladas → 323  ✓ (+13)
 *
 * Ou seja: com a palavra inteira o último botão fica 7px FORA da faixa — que é
 * o #191 outra vez, só que aqui quem não cabe é o texto (a contagem já tinha
 * saído para o `aria-label`). Entre as três que cabem, "Andam." é a única que
 * não inventa uma SEGUNDA palavra para o mesmo estado: o selo do próprio card,
 * uma linha abaixo, diz "Em andamento", e "Em curso"/"Execução" ali leriam como
 * outro status (#191). É também a abreviação que o app já usa para este mesmo
 * status no gráfico de OS do relatório (#84).
 *
 * `srLabel` é o nome COMPLETO, e não é enfeite: ele é o que vai para o
 * `aria-label`/`title`, onde não custa pixel nenhum — quem usa leitor de tela
 * ouve "Em andamento", não "Andam." (#142/#191).
 */
export const SERVICE_ORDER_STATUS_FILTER_OPTIONS: readonly {
  value: ServiceOrderStatusFilter;
  label: string;
  srLabel: string;
}[] = [
  { value: 'all', label: 'Todas', srLabel: 'Todas' },
  { value: 'pending', label: 'Pendentes', srLabel: 'Pendentes' },
  { value: 'in_progress', label: 'Andam.', srLabel: 'Em andamento' },
  { value: 'completed', label: 'Concluídas', srLabel: 'Concluídas' },
  { value: 'cancelled', label: 'Canceladas', srLabel: 'Canceladas' },
];

export function matchesServiceOrderStatus(
  order: ServiceOrderRow,
  filter: ServiceOrderStatusFilter,
): boolean {
  if (filter === 'all') return true;
  // Linha sem status é PENDENTE — é o mesmo default que a tabela e o card usam
  // (`status ?? 'pending'`). Tratá-la como "sem status" a esconderia do único
  // botão que deveria trazê-la.
  return (order?.status ?? 'pending') === filter;
}

/**
 * Quantas ordens cada botão traria — contadas sobre o recorte JÁ filtrado por
 * busca, período e valor, mas ANTES do status.
 *
 * É a única definição que não mente: contar sobre a lista inteira faria o botão
 * prometer 12 com uma busca ativa que devolve 3, e contar depois do status
 * zeraria todos os outros botões (#193/#196).
 */
export function countServiceOrdersByStatus(
  orders: readonly ServiceOrderRow[],
): Record<ServiceOrderStatusFilter, number> {
  const counts: Record<ServiceOrderStatusFilter, number> = {
    all: orders.length,
    pending: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
  };
  for (const order of orders) {
    const status = String(order?.status ?? 'pending') as ServiceOrderStatus;
    if (status in counts && status !== ('all' as string)) counts[status] += 1;
  }
  return counts;
}

// ─── Período e faixa de valor (o modal) ─────────────────────────────────────

/**
 * Presets oferecidos no modal. Subconjunto EXPLÍCITO dos de `reportPeriod`, o
 * mesmo de `/quotes`, `/clients` e `/trabalhos`: os longos de relatório não
 * ajudam a achar uma ordem, e `custom` exigiria um calendário dentro do modal.
 */
export const SERVICE_ORDER_PERIOD_PRESETS: readonly ReportPeriodPreset[] = [
  'today',
  'thisWeek',
  'thisMonth',
  '30d',
  '3m',
  'thisYear',
];

/**
 * `range` nulo = sem filtro de período. Ordem SEM data legível NÃO é recortada:
 * sumir com ela por causa de um carimbo estranho é pior do que mostrá-la fora da
 * faixa pedida — o usuário não teria como saber que ela existe (#193-d).
 */
export function matchesServiceOrderPeriod(
  order: ServiceOrderRow,
  range: ReportDateRange | null,
): boolean {
  if (!range) return true;
  const time = new Date(order?.created_at ?? '').getTime();
  if (!Number.isFinite(time)) return true;
  return time >= range.from.getTime() && time <= range.to.getTime();
}

/** `null` em `min`/`max` é SEM LIMITE, nunca zero (ver `listFilters`). */
export function matchesServiceOrderValue(order: ServiceOrderRow, range: ListValueRange): boolean {
  return matchesListRange(order?.total, range);
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * O STATUS fica de fora: ele tem os botões visíveis logo acima, então já se
 * anuncia sozinho. Contá-lo faria o selo dizer "1" com o modal em branco.
 */
export function countActiveServiceOrderFilters(
  period: ReportPeriodPreset | null,
  range: ListValueRange,
): number {
  let active = 0;
  if (period) active += 1;
  if (range.min !== null || range.max !== null) active += 1;
  return active;
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

/** O status que NÃO representa dinheiro vivo. */
export const DEAD_SERVICE_ORDER_STATUSES: ReadonlySet<string> = new Set<ServiceOrderStatus>([
  'cancelled',
]);

/** `true` quando o recorte É exatamente o status morto (ver `includeDead`). */
export function isDeadServiceOrderStatusFilter(filter: ServiceOrderStatusFilter): boolean {
  return DEAD_SERVICE_ORDER_STATUSES.has(filter);
}

export interface ServiceOrderListSummary {
  /** Ordens no recorte — todas, inclusive as canceladas. */
  count: number;
  /** Soma das que ainda representam dinheiro (fora as canceladas). */
  total: number;
  /** Quantas ficaram FORA da soma. Nunca cortado em silêncio (#61). */
  excludedCount: number;
}

export interface SummarizeServiceOrdersOptions {
  /**
   * Soma TAMBÉM as canceladas.
   *
   * Existe por causa do filtro explícito por "Canceladas": excluir todo mundo
   * ali faria o hero anunciar R$ 0,00 com a lista cheia embaixo — o número mais
   * visível da tela contradizendo o que ela mostra. A exclusão serve para a
   * faixa "Todas" não INFLAR o que o prestador tem a receber; quando ele pede
   * justamente as canceladas, a pergunta mudou.
   */
  includeDead?: boolean;
}

/** Resumo do recorte JÁ FILTRADO (busca, status, período e valor aplicados). */
export function summarizeServiceOrderList(
  orders: readonly ServiceOrderRow[],
  options: SummarizeServiceOrdersOptions = {},
): ServiceOrderListSummary {
  let total = 0;
  let excludedCount = 0;

  for (const order of orders) {
    if (!options.includeDead && DEAD_SERVICE_ORDER_STATUSES.has(String(order?.status))) {
      excludedCount += 1;
      continue;
    }
    total += quoteTotalNumber(order?.total);
  }

  return { count: orders.length, total, excludedCount };
}

/**
 * O RÓTULO do valor do hero — é ele que torna o número honesto.
 *
 * A soma sai do recorte já filtrado (busca + status + período + valor), então
 * ela muda enquanto o usuário digita. Sem dizer de qual recorte ela saiu,
 * "R$ 12.400,00" sob o título "Ordens de serviço" lê como "é isso que eu tenho"
 * quando pode ser o recorte de canceladas com uma busca ativa (#182/#188).
 */
export function serviceOrderListTotalLabel(filter: ServiceOrderStatusFilter): string {
  switch (filter) {
    case 'pending':
      return 'Total pendente';
    case 'in_progress':
      return 'Total em andamento';
    case 'completed':
      return 'Total concluído';
    // O status morto ganha rótulo PRÓPRIO porque, sob o filtro dele, a soma
    // passa a incluí-lo (`includeDead`): dizer "Total em ordens" ali faria o
    // número parecer receita quando ele é justamente o oposto.
    case 'cancelled':
      return 'Total cancelado';
    default:
      return 'Total em ordens de serviço';
  }
}
